import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loading } from '@/shared/components/feedback';
import { AppShell } from '@/shared/layouts';
import { ProtectedRoute } from '@/shared/permissions';

// Lazy load pages for code splitting
const LoginPage = React.lazy(() => import('@/routes/auth/LoginPage'));
const SignupPage = React.lazy(() => import('@/routes/auth/SignupPage'));
const OverviewPage = React.lazy(() => import('@/routes/app/OverviewPage'));
const LandingPage = React.lazy(() => import('@/routes/marketing/LandingPage'));
const NotFoundPage = React.lazy(() => import('@/routes/NotFoundPage'));

// Admin pages
const AdminLayout = React.lazy(() => import('@/routes/app/admin/AdminLayout'));
const AdminOverviewPage = React.lazy(() => import('@/routes/app/admin/AdminOverviewPage'));
const OrganizationsPage = React.lazy(() => import('@/routes/app/admin/OrganizationsPage'));
const TeamsPage = React.lazy(() => import('@/routes/app/admin/TeamsPage'));
const TeamDetailPage = React.lazy(() => import('@/routes/app/admin/TeamDetailPage'));
const UsersPage = React.lazy(() => import('@/routes/app/admin/UsersPage'));
const RolesPage = React.lazy(() => import('@/routes/app/admin/RolesPage'));
const AuditLogPage = React.lazy(() => import('@/routes/app/admin/AuditLogPage'));

// Client pages
const ClientsLayout = React.lazy(() => import('@/routes/app/clients/ClientsLayout'));
const ClientListPage = React.lazy(() => import('@/routes/app/clients/ClientListPage'));
const ClientDetailPage = React.lazy(() => import('@/routes/app/clients/ClientDetailPage'));
const ClientOverviewTab = React.lazy(() => import('@/routes/app/clients/tabs/ClientOverviewTab'));
const AdAccountsTab = React.lazy(() => import('@/routes/app/clients/tabs/AdAccountsTab'));
const WorkspacesTab = React.lazy(() => import('@/routes/app/clients/tabs/WorkspacesTab'));
const ClientUsersTab = React.lazy(() => import('@/routes/app/clients/tabs/ClientUsersTab'));
const ClientTeamsTab = React.lazy(() => import('@/routes/app/clients/tabs/ClientTeamsTab'));
const DataSourcesTab = React.lazy(() => import('@/routes/app/clients/tabs/DataSourcesTab'));
const ActivityTab = React.lazy(() => import('@/routes/app/clients/tabs/ActivityTab'));
const SettingsTab = React.lazy(() => import('@/routes/app/clients/tabs/SettingsTab'));
const AudiencesTab = React.lazy(() => import('@/routes/app/clients/tabs/AudiencesTab'));
const CampaignsTab = React.lazy(() => import('@/routes/app/clients/tabs/CampaignsTab'));
const TrackingTab = React.lazy(() => import('@/routes/app/clients/tabs/TrackingTab'));
const IntegrationsTab = React.lazy(() => import('@/routes/app/clients/tabs/IntegrationsTab'));
const ReportsTab = React.lazy(() => import('@/routes/app/clients/tabs/ReportsTab'));
const OnboardingWizard = React.lazy(() => import('@/routes/app/clients/onboarding/OnboardingWizard'));

function App() {
  return (
    <Suspense fallback={<Loading fullScreen />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />

        {/* Redirect root to app */}
        <Route path="/" element={<LandingPage />} />

        {/* Protected app routes */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          {/* Default redirect to overview */}
          <Route index element={<Navigate to="/app/overview" replace />} />

          {/* Overview - fully implemented */}
          <Route
            path="overview"
            element={
              <ProtectedRoute requiredPermission="view:dashboard">
                <OverviewPage />
              </ProtectedRoute>
            }
          />

          {/* Client Management */}
          <Route
            path="clients"
            element={
              <ProtectedRoute requiredPermission="view:clients">
                <ClientsLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<ClientListPage />} />
            <Route
              path="new"
              element={
                <ProtectedRoute requiredPermission="manage:clients">
                  <OnboardingWizard />
                </ProtectedRoute>
              }
            />
            <Route path=":id" element={<ClientDetailPage />}>
              <Route index element={<ClientOverviewTab />} />
              <Route path="workspaces" element={<WorkspacesTab />} />
              <Route path="users" element={<ClientUsersTab />} />
              <Route path="teams" element={<ClientTeamsTab />} />
              <Route path="data-sources" element={<DataSourcesTab />} />
              <Route path="ad-accounts" element={<AdAccountsTab />} />
              <Route path="integrations" element={<IntegrationsTab />} />
              <Route path="tracking" element={<TrackingTab />} />
              <Route path="audiences" element={<AudiencesTab />} />
              <Route path="campaigns" element={<CampaignsTab />} />
              <Route path="reports" element={<ReportsTab />} />
              <Route path="settings" element={<SettingsTab />} />
              <Route path="activity" element={<ActivityTab />} />
            </Route>
          </Route>

          {/* Removed top-level modules (F2.5): redirect legacy links */}
          <Route path="data" element={<Navigate to="/app/overview" replace />} />
          <Route path="tracking" element={<Navigate to="/app/overview" replace />} />
          <Route path="audiences" element={<Navigate to="/app/overview" replace />} />
          <Route path="activations" element={<Navigate to="/app/overview" replace />} />
          <Route path="campaigns" element={<Navigate to="/app/overview" replace />} />
          <Route path="analytics" element={<Navigate to="/app/overview" replace />} />
          <Route path="integrations" element={<Navigate to="/app/overview" replace />} />
          <Route path="reports" element={<Navigate to="/app/overview" replace />} />
          <Route path="settings" element={<Navigate to="/app/overview" replace />} />
          {/* Admin section with nested routes */}
          <Route
            path="admin"
            element={
              <ProtectedRoute requiredPermission="view:admin">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminOverviewPage />} />
            <Route path="organizations" element={<OrganizationsPage />} />
            <Route path="teams" element={<TeamsPage />} />
            <Route path="teams/:id" element={<TeamDetailPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="roles" element={<RolesPage />} />
            <Route path="audit" element={<AuditLogPage />} />
          </Route>
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;
